import DropdownButton from "../../../controls/DropdownButton";
import FlexLayout from "../../../controls/FlexLayout";
import Icon from "../../../controls/Icon";
import { IconButton } from "../../../controls/TooltipControls";
import { ComponentEx, connect, translate } from "../../../controls/ComponentEx";
import Debouncer from "../../../util/Debouncer";
import { getWindowId } from "../../../util/preloadAccess";
import { getSafe } from "../../../util/storeHelper";
import MainPage from "../../../views/MainPage";
import { setDashletEnabled, setDashletHeight, setDashletWidth, setLayout, } from "../actions";
import FixedItem from "./FixedItem";
import PackeryGrid from "./PackeryGrid";
import PackeryItem from "./PackeryItem";
import * as _ from "lodash";
import * as React from "react";
import { Button, MenuItem } from "react-bootstrap";
const UPDATE_FREQUENCY_MS = 1000;
function BackgroundGrid(props) {
    return props.editMode ? (React.createElement("div", { key: "background-grid", className: "dashboard-background-grid" })) : (React.createElement("div", { key: "background-grid" }));
}
/**
 * base layouter for the dashboard. No own content, just layouting
 */
class Dashboard extends ComponentEx {
    mUpdateTimer;
    mLayoutDebouncer;
    mWindowFocused = true;
    mUnsubscribeFocus;
    mUnsubscribeBlur;
    constructor(props) {
        super(props);
        this.initState({
            counter: 0,
            editMode: false,
        });
        this.mLayoutDebouncer = new Debouncer((layout) => {
            if (!_.isEqual(layout, this.props.layout)) {
                this.props.onSetLayout(layout);
            }
            return null;
        }, 500);
    }
    componentDidMount() {
        this.startUpdateCycle();
        // Check initial focus state
        window.api.window.isFocused(getWindowId()).then((focused) => {
            this.mWindowFocused = focused;
        });
        // Subscribe to focus/blur events via preload API
        this.mUnsubscribeFocus = window.api.window.onFocus(this.onFocus);
        this.mUnsubscribeBlur = window.api.window.onBlur(this.onBlur);
    }
    componentWillUnmount() {
        clearTimeout(this.mUpdateTimer);
        // Unsubscribe from focus/blur events
        this.mUnsubscribeFocus?.();
        this.mUnsubscribeBlur?.();
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.active !== nextProps.active) {
            if (nextProps.active && this.mUpdateTimer === undefined) {
                this.startUpdateCycle();
            }
            else if (!nextProps.active && this.mUpdateTimer !== undefined) {
                clearTimeout(this.mUpdateTimer);
                this.mUpdateTimer = undefined;
            }
        }
    }
    render() {
        const { t, dashletSettings, layout, dashlets } = this.props;
        const { editMode } = this.state;
        const state = this.context.api.store.getState();
        const layoutMap = layout.reduce((prev, key, idx) => {
            prev[key] = idx + 1;
            return prev;
        }, {});
        // Dashlets not in the layout (e.g. just re-enabled) are appended after
        // all existing items so Packery places them in the first available gap
        // instead of overlapping with items that already claimed their old position.
        const layoutSize = layout.length;
        const sorted = dashlets
            .filter((dash) => (dash.isVisible === undefined || dash.isVisible(state)) &&
            (!dash.closable ||
                getSafe(dashletSettings, [dash.title, "enabled"], true)))
            .sort((lhs, rhs) => (layoutMap[lhs.title] ?? layoutSize + lhs.position) -
            (layoutMap[rhs.title] ?? layoutSize + rhs.position));
        const { fixed, dynamic } = sorted.reduce((prev, dash) => {
            const isFixed = getSafe(dashletSettings, [dash.title, "fixed"], dash.fixed);
            prev[isFixed ? "fixed" : "dynamic"].push(dash);
            return prev;
        }, { fixed: [], dynamic: [] });
        const classes = [
            "page-dashboard",
            editMode ? "dashboard-edit" : "dashboard-view",
        ];
        return (React.createElement(MainPage, { id: "page-dashboard", className: classes.join(" ") },
            React.createElement(MainPage.Body, { style: { display: "flex", flexDirection: "column" } },
                React.createElement("div", { className: "dashboard-editbar" }, this.renderEditBar()),
                React.createElement("div", { className: "fixed-dashlets" }, fixed.map(this.renderFixedItem)),
                React.createElement("div", { className: "dynamic-dashlets" },
                    React.createElement(PackeryGrid, { totalWidth: 3, onChangeLayout: this.onChangeLayout, settings: dashletSettings, items: dynamic.map((iter) => iter.title).sort() },
                        React.createElement(BackgroundGrid, { editMode: editMode }),
                        dynamic.map(this.renderItem))))));
    }
    renderEditBar() {
        const { t, dashlets, dashletSettings } = this.props;
        const { editMode } = this.state;
        const state = this.context.api.store.getState();
        return editMode ? (React.createElement(FlexLayout, { type: "row" },
            React.createElement(FlexLayout.Fixed, null, t("Drag dashlets to rearrange and drag the borders to resize")),
            React.createElement(FlexLayout.Flex, null),
            React.createElement(FlexLayout.Fixed, null,
                React.createElement(DropdownButton, { id: "add-widget-button", title: t("Add Dashlet") }, dashlets
                    .filter((dash) => dash.closable && dash.isVisible?.(state) !== false)
                    .map((dash) => (React.createElement(MenuItem, { onClick: this.toggleMenuItem, "data-id": dash.title, key: dash.title },
                    React.createElement(Icon, { name: dashletSettings[dash.title]?.enabled === false
                            ? "checkbox-unchecked"
                            : "checkbox-checked" }),
                    " ",
                    dash.title)))),
                React.createElement(Button, { onClick: this.toggleEdit }, t("Done"))))) : (React.createElement("div", { className: "dashlet-customize-btn" },
            React.createElement(IconButton, { icon: "edit", tooltip: t("Customize your dashboard"), onClick: this.toggleEdit }, t("Customize your dashboard"))));
    }
    toggleMenuItem = (evt) => {
        evt.stopPropagation();
        const { dashletSettings } = this.props;
        const dashId = evt.currentTarget.getAttribute("data-id");
        const old = dashletSettings[dashId]?.enabled !== false;
        if (old) {
            // Clear pending layout save so it can't restore a stale position
            // for this dashlet after the reducer has already removed it.
            this.mLayoutDebouncer.clear();
        }
        this.props.onSetDashletEnabled(dashId, !old);
    };
    toggleEdit = () => {
        this.nextState.editMode = !this.nextState.editMode;
    };
    onChangeLayout = (layout) => {
        this.mLayoutDebouncer.schedule(undefined, layout);
    };
    onFocus = () => {
        this.mWindowFocused = true;
    };
    onBlur = () => {
        this.mWindowFocused = false;
    };
    startUpdateCycle = () => {
        // TODO: this is a hack needed so dashlets get updated even if they get props passed in
        //   in a way that doesn't properly signal for an update.
        //   it should be possible to make this unnecessary with makeReactive, but that requires
        //   testing
        this.mUpdateTimer = setTimeout(() => {
            if (this.mWindowFocused &&
                process.env["DEBUG_REACT_RENDERS"] !== "true") {
                this.nextState.counter++;
            }
            this.startUpdateCycle();
        }, UPDATE_FREQUENCY_MS);
    };
    renderFixedItem = (dash) => {
        const { t } = this.props;
        const { counter, editMode } = this.state;
        const componentProps = dash.props !== undefined ? dash.props() : {};
        return (React.createElement(FixedItem, { t: t, id: dash.title, key: dash.title, width: dash.width, height: dash.height, editable: editMode, onDismiss: dash.closable ? this.dismissDashlet : undefined },
            React.createElement(dash.component, { t: this.props.t, ...componentProps, counter: counter })));
    };
    renderItem = (dash, idx) => {
        const { t, dashletSettings } = this.props;
        const { counter, editMode } = this.state;
        const componentProps = dash.props !== undefined ? dash.props() : {};
        return (React.createElement(PackeryItem, { t: t, id: dash.title, key: dash.title, closable: dash.closable, editable: editMode, position: idx + 1, width: dashletSettings?.[dash.title]?.width ?? dash.width, height: dashletSettings?.[dash.title]?.height ?? dash.height, onSetWidth: this.setWidth, onSetHeight: this.setHeight, onDismiss: dash.closable ? this.dismissDashlet : undefined, fixed: dash.fixed },
            React.createElement(dash.component, { t: this.props.t, ...componentProps, counter: counter })));
    };
    setWidth = (id, width) => {
        this.props.onSetDashletWidth(id, width);
    };
    setHeight = (id, height) => {
        this.props.onSetDashletHeight(id, height);
    };
    dismissDashlet = (dashletId) => {
        this.mLayoutDebouncer.clear();
        this.props.onSetDashletEnabled(dashletId, false);
    };
}
function mapStateToProps(state) {
    return {
        // Defensive checks: interface might not be initialized during hydration
        layout: state.settings?.interface?.dashboardLayout ?? [],
        dashletSettings: state.settings?.interface?.dashletSettings ?? {},
    };
}
function mapDispatchToProps(dispatch) {
    return {
        onSetLayout: (items) => dispatch(setLayout(items)),
        onSetDashletEnabled: (dashletId, enabled) => dispatch(setDashletEnabled(dashletId, enabled)),
        onSetDashletWidth: (dashletId, width) => dispatch(setDashletWidth(dashletId, width)),
        onSetDashletHeight: (dashletId, height) => dispatch(setDashletHeight(dashletId, height)),
    };
}
export default translate(["common"])(connect(mapStateToProps, mapDispatchToProps)(Dashboard));
